/**
 * The made-to-measure prices per style code and currency, loaded page by page,
 * with the CSV import / export. Admin only.
 */
import { useEffect, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDown,
  faArrowUp,
  faCircleNotch,
  faDownload,
  faSearch,
  faUpload,
} from '@fortawesome/free-solid-svg-icons';
import { MTM_ROUTE_PATH, PLATFORM, ROLES } from '@/shared/constants';
import { getUserProfile } from '@/features/auth/session';
import { hardNavigate } from '@/shared/lib/navigation';
import { TopMenu } from '@/shared/layout/TopMenu';
import { snackbar } from '@/shared/ui/snackbar';
import { useDmsCsv } from '../use-dms-csv';
import { lastLogQueryOptions } from '../api';
import { pricePagesQueryOptions } from './api';
import { formatCsvDate } from '../fabrics/fabric-form';
import {
  csvRowsToPrices,
  priceColumnCount,
  priceColumns,
  priceCsvFileName,
} from './price-logic';
import { ariaSort } from '@/shared/ui/aria-sort';
import { kebabCase } from '@/shared/lib/kebab-case';

const COLUMNS: { label: string; sortBy: string; testId: string }[] = [
  { label: 'Style Code', sortBy: 'data.styleCode', testId: 'style-code' },
];

export function PricesPage() {
  const user = getUserProfile();
  const isAdmin = user?.role === ROLES.ADMIN;

  useEffect(() => {
    if (user && !isAdmin) hardNavigate(`/${MTM_ROUTE_PATH}/main`);
  }, [user, isAdmin]);

  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  // The sort starts on `styleCode`, a name no header uses: no arrow until a header is clicked (see migration.md).
  const [sort, setSort] = useState<{ by: string | null; ascending: boolean }>({
    by: 'styleCode',
    ascending: true,
  });
  const [refresh, setRefresh] = useState(0);
  const [columnCount, setColumnCount] = useState(0);

  const prices = useInfiniteQuery({
    ...pricePagesQueryOptions({
      search: appliedSearch,
      sortBy: sort.by,
      sortOrder: sort.ascending ? 'asc' : 'desc',
      refresh,
    }),
    enabled: isAdmin,
  });
  useEffect(() => {
    if (prices.isError)
      snackbar('Failed to load items, please try again later');
  }, [prices.isError]);
  const lastUpload = useQuery({
    ...lastLogQueryOptions('dmsPriceCsvUpload', refresh),
    enabled: isAdmin,
  });

  const items = prices.data?.pages.flatMap((page) => page.data) ?? [];
  const nextCursor = prices.data?.pages.at(-1)?.nextCursor ?? null;
  // The colspan of the footer is frozen on the first load only (see migration.md).
  if (columnCount === 0 && items.length)
    setColumnCount(priceColumnCount(items));
  const reload = () => setRefresh((count) => count + 1);

  const { csvRows, csvInput, uploadCsv, downloadCsv, onCsvChosen } = useDmsCsv({
    type: 'price',
    label: 'prices',
    logType: 'dmsPriceCsvUpload',
    toItems: csvRowsToPrices,
    exportFileName: priceCsvFileName,
    onUploaded: reload,
  });

  if (!isAdmin) return null;

  const columns = priceColumns(items);

  return (
    <div className={uploadCsv.isPending ? 'overlay' : ''}>
      <div className="container summary">
        <TopMenu platform={PLATFORM.DMS} />
        <div className="summary-table mb-12 mt-4">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <h2>Prices</h2>
              <form
                className="input-group mt-2"
                style={{ maxWidth: 300 }}
                onSubmit={(event) => {
                  event.preventDefault();
                  setAppliedSearch(search);
                }}
              >
                <input
                  type="text"
                  className="form-control shadow-none"
                  placeholder="Search"
                  aria-label="Search"
                  data-testid="mtm-price-search-input"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
                <button
                  type="submit"
                  className="btn text-muted bg-white px-3 shadow-none"
                  style={{ border: '1px solid #ced4da' }}
                  aria-label="Search"
                  data-testid="mtm-price-search-button"
                >
                  <FontAwesomeIcon icon={faSearch} />
                </button>
              </form>
              <div className="search-hint">
                To activate your search, please click the magnifying glass icon
                or press Enter.
              </div>
            </div>

            <div className="flex flex-col items-end">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faUpload} style={{ fontSize: 24 }} />
                <input
                  ref={csvInput}
                  className="field field-upload"
                  name="csv-upload"
                  id="csv-upload"
                  aria-label="Prices CSV file"
                  data-testid="mtm-price-upload-input"
                  type="file"
                  accept=".csv"
                  style={{ height: 30 }}
                  onChange={(event) => onCsvChosen(event.target.files?.[0])}
                />
                <button
                  type="button"
                  className="button button-form"
                  style={{ height: 30 }}
                  data-testid="mtm-price-upload-button"
                  disabled={uploadCsv.isPending || !csvRows}
                  onClick={() => uploadCsv.mutate()}
                >
                  Save
                </button>
              </div>
              <div className="mt-1" data-testid="mtm-price-upload-last-date">
                Last updated date: {formatCsvDate(lastUpload.data?.createdAt)}
              </div>
              <div className="mt-1" data-testid="mtm-price-upload-file-name">
                File name: {lastUpload.data?.filename || '-'}
              </div>
              <div className="flex items-center">
                <button
                  type="button"
                  className="button button-form"
                  data-testid="mtm-price-download-button"
                  onClick={() => downloadCsv.mutate()}
                >
                  Download prices in CSV
                </button>
                <FontAwesomeIcon
                  icon={downloadCsv.isPending ? faCircleNotch : faDownload}
                  spin={downloadCsv.isPending}
                  style={{ fontSize: 24, marginLeft: 10 }}
                />
              </div>
            </div>
          </div>
          <div className="mb-6">
            <div className="table-container dms-prices">
              <div
                className="items-container"
                role="region"
                aria-label="Prices"
              >
                <table className="table items">
                  <thead>
                    <tr>
                      {COLUMNS.map(({ label, sortBy, testId }) => (
                        <th
                          key={sortBy}
                          style={{ width: 130 }}
                          aria-sort={ariaSort(
                            sort.by === sortBy,
                            !sort.ascending,
                          )}
                        >
                          <button
                            type="button"
                            className="cursor-pointer"
                            data-testid={`mtm-price-header-${testId}`}
                            onClick={() =>
                              setSort((current) => ({
                                by: sortBy,
                                ascending: !current.ascending,
                              }))
                            }
                          >
                            {label}{' '}
                            {sort.by === sortBy && (
                              <FontAwesomeIcon
                                icon={sort.ascending ? faArrowUp : faArrowDown}
                              />
                            )}
                          </button>
                        </th>
                      ))}
                      {columns.map((col) => (
                        <th
                          key={col}
                          style={{ width: 130 }}
                          data-testid={`mtm-price-header-${kebabCase(col)}`}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {prices.isPending ? (
                      <tr>
                        <td colSpan={4} className="py-12 text-center">
                          <FontAwesomeIcon
                            icon={faCircleNotch}
                            spin
                            size="2x"
                            className="text-muted"
                          />
                          <div className="text-muted mt-2">
                            Loading items...
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <>
                        {items.map((price) => (
                          <tr key={price.id} className="user-row">
                            <td
                              style={{ width: 130 }}
                              title={String(price.data.styleCode ?? '')}
                              data-testid="mtm-price-row-style-code"
                            >
                              {String(price.data.styleCode ?? '')}
                            </td>
                            {columns.map((col) => (
                              <td
                                key={col}
                                style={{ width: 130 }}
                                data-testid={`mtm-price-row-${kebabCase(col)}`}
                              >
                                {String(price.data[col] ?? '')}
                              </td>
                            ))}
                          </tr>
                        ))}
                        <tr>
                          <td
                            colSpan={columnCount || undefined}
                            className="py-1! text-left!"
                          >
                            {prices.isFetchingNextPage && (
                              <div className="text-muted">
                                <FontAwesomeIcon icon={faCircleNotch} spin />{' '}
                                Loading more items...
                              </div>
                            )}
                            {nextCursor ? (
                              <button
                                type="button"
                                className="btn btn-link shadow-none"
                                data-testid="mtm-price-load-more-button"
                                onClick={() => void prices.fetchNextPage()}
                              >
                                Load more items...
                              </button>
                            ) : (
                              <span className="text-muted">No more items</span>
                            )}
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
